<template>
  <div
    v-if="groups.length"
    class="profile-system__wrapper"
  >
    <div class="profile-content full-size">
      <up-card
        v-for="(group, gindex) of groups"
        :key="`rg-${gindex}`"
        class="rubrics-card"
      >
        <h3 class="rubrics-card__title">
          {{ group.label }}
        </h3>
        <div class="rubrics-list">
          <div
            v-for="(item, iindex) of group.values"
            :key="`ri-${iindex}`"
            :class="{ 'rubrics-list__item--split': item.tail }"
            :style="{ order: orderOf(gindex, iindex) }"
            class="rubrics-list__item"
          >
            <b class="rubrics-list__head">{{ item.head }}</b>
            <span
              v-if="item.tail"
              class="rubrics-list__tail"
            >{{ item.tail }}</span>
          </div>
        </div>
      </up-card>
    </div>
  </div>
</template>

<script>
import { parseRubrics } from '@/components/mixins/profileRubrics';

/**
 * Интересы человека — поле `rubrics` из карточки участника.
 *
 * Один блок на два экрана: профиль другого участника и свой профиль. Принимает
 * ответ сервера как есть, разбор — в profileRubrics.js. Нечего показать
 * (ключа нет, список пуст) — блока на экране не будет.
 *
 * Зарегистрирован локально в обоих профилях, а не в library/index.js:
 * переиспользуется в двух местах, а не повсеместно.
 */
/** Зазор между плашками — тот же, что в `gap` списка. */
const GAP = 8;

export default {
  name: 'ProfileRubrics',
  props: {
    rubrics: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      // порядок плашек внутри каждой группы: orders[группа][значение] = позиция.
      // Пока не посчитан, у всех ноль — рисуется порядок из parseRubrics.
      orders: [],
      // ширина, при которой считали. Наблюдатель следит и за высотой, а она
      // меняется от самой перестановки — без этой проверки был бы вечный круг.
      lastWidth: 0,
    };
  },
  computed: {
    groups() {
      // заголовок группы приходит с сервера сразу на двух языках,
      // своей строки в локали для него нет
      return parseRubrics(this.rubrics, this.$store.getters.getLang);
    },
  },
  watch: {
    groups() {
      // язык переключили или карточка догрузилась — раскладку считаем заново
      this.lastWidth = 0;
      this.$nextTick(this.measure);
    },
  },
  mounted() {
    this.$nextTick(this.measure);
    if (window.ResizeObserver) {
      this.observer = new ResizeObserver(this.measure);
      this.observe();
    }
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect();
  },
  methods: {
    orderOf(groupIndex, valueIndex) {
      return (this.orders[groupIndex] || [])[valueIndex] || 0;
    },
    /** Наблюдаем за корнем: блока может не быть — тогда там комментарий. */
    observe() {
      if (!this.observer || !this.$el || this.$el.nodeType !== Node.ELEMENT_NODE) return;
      this.observer.observe(this.$el);
    },
    measure() {
      this.observe();
      if (!this.$el || typeof this.$el.querySelectorAll !== 'function') return;
      const width = this.$el.clientWidth;
      // ширина не изменилась — раскладка та же, считать нечего
      if (!width || width === this.lastWidth) return;
      this.lastWidth = width;
      this.orders = [...this.$el.querySelectorAll('.rubrics-list')].map(this.packList);
    },
    /**
     * Раскладывает плашки одного списка по строкам и возвращает позиции.
     *
     * Плашки с уточнением занимают строку целиком, они идут первыми и в том
     * порядке, в каком пришли. Остальные раскладываем от широкой к узкой,
     * добирая в строку всё, что в неё ещё влезает: так строка заполняется
     * плотно и в ней не остаётся одинокой плашки, которую растянуло бы во всю
     * ширину. Порядок применяется свойством `order`, DOM не трогаем.
     */
    packList(list) {
      const width = list.clientWidth;
      if (!width) return [];

      // растягивание мешает узнать собственную ширину плашки — гасим на замер
      list.classList.add('rubrics-list--measuring');
      const items = [...list.children]
        .filter((el) => el.classList.contains('rubrics-list__item'))
        .map((el, index) => ({
          index,
          split: el.classList.contains('rubrics-list__item--split'),
          width: el.getBoundingClientRect().width,
        }));
      list.classList.remove('rubrics-list--measuring');

      const order = items.map(() => 0);
      let position = 0;
      items.filter((item) => item.split).forEach((item) => {
        order[item.index] = position;
        position += 1;
      });

      const rows = [];
      items
        .filter((item) => !item.split)
        .sort((a, b) => b.width - a.width)
        .forEach((item) => {
          // полпикселя запаса: ширины дробные, и точное равенство роняет
          // плашку на следующую строку уже после раскладки
          const row = rows.find((r) => r.used + GAP + item.width <= width - 0.5);
          if (row) {
            row.used += GAP + item.width;
            row.items.push(item);
          } else {
            rows.push({ used: item.width, items: [item] });
          }
        });
      rows.forEach((row) => row.items.forEach((item) => {
        order[item.index] = position;
        position += 1;
      }));

      return order;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/style/variables.scss';

.profile-system__wrapper {
  display: flex;
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  align-items: center;
  flex: 0 1 auto;
  align-self: stretch;
}
.profile-content {
  display: flex;
  padding: 0 12px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  &.full-size {
    width: 100%;
  }
}
.rubrics-card {
  width: 100%;
  border-radius: 14px;

  :deep(.upCard__inner) {
    gap: 12px;
  }

  &__title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
  }
}
.rubrics-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  // Распорка забирает остаток последней строки. Без неё одинокий тег в конце
  // растянулся бы во всю ширину и выглядел бы как оборванный заголовок —
  // так же, как последняя строка в тексте по формату не растягивается.
  // flex-grow заведомо больше, чем у тегов: остаток достаётся ей целиком.
  // order обязателен: плашкам его расставляет раскладка, и распорка с нулём
  // уехала бы в начало списка вместо конца.
  &::after {
    content: '';
    order: 9999;
    flex: 999 1 0;
  }

  // На время замера растягивание выключено: нужна собственная ширина плашки.
  // Класс держится доли миллисекунды и снимается в том же обработчике.
  &--measuring &__item {
    flex-grow: 0;
  }

  &__item {
    // Теги делят остаток своей строки между собой, поэтому правый край
    // ровный, а не зубчатый. Сжиматься разрешено: значение шире строки
    // должно переносить текст внутри себя, а не вылезать за карточку.
    flex: 1 1 auto;
    padding: 8px 12px;
    border-radius: 12px;
    background: rgba($color-primary, .09);
    font-size: 13px;
    line-height: 1.3;

    // Уточнение нашлось — тег занимает строку целиком.
    &--split {
      flex-basis: 100%;
    }
  }
  &__head {
    display: block;
    color: $color-primary;
    font-weight: 700;
  }
  &__tail {
    display: block;
    color: $color-disabled;
  }
}
</style>
